import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Bot, User } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AiInsightsPage() {
  const [messages, setMessages] = useState([
    {
      role: "ai",
      content:
        "Hi — I'm your CX10X analyst, powered by GPT-5.2 over your live response data. Ask about trends, themes, root causes, or recommended actions. Try one of the suggestions below.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/ai/chat", { message: q });
      setMessages((m) => [...m, { role: "ai", content: data.reply, context: data.context }]);
    } catch (e) {
      setErr(formatApiError(e));
      setMessages((m) => [
        ...m,
        { role: "ai", content: "I couldn't reach the analytics engine. Please try again in a moment." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const suggestions = [
    "Why did NPS drop last week?",
    "Top 5 themes from detractors in the last 30 days",
    "What should I fix first?",
    "Which surveys are underperforming?",
    "Summarize loyalty by industry",
    "Draft a closed-loop email for our top detractors",
  ];

  return (
    <div className="space-y-5 fade-in max-w-4xl">
      <Card className="p-5 border-[#FFE1BE] bg-[#FFF0DF]/50">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F58220] text-white grid place-items-center">
            <Sparkles size={20} />
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-[#1F3A6B]">Conversational AI Analyst — GPT-5.2</h2>
            <p className="text-sm text-slate-600">
              Ask anything in plain English. The analyst sees your live responses and comments and surfaces themes, root cause, and recommended actions.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-0 border-slate-100 min-h-[480px] flex flex-col overflow-hidden">
        <div ref={scrollRef} className="flex-1 p-5 space-y-4 overflow-y-auto max-h-[60vh]">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "ai" && (
                <div className="w-8 h-8 rounded-md bg-[#F58220] text-white grid place-items-center shrink-0">
                  <Bot size={16} />
                </div>
              )}
              <div
                data-testid={`ai-msg-${i}`}
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#F58220] text-white" : "bg-slate-100 text-slate-800 border border-slate-200"
                }`}
              >
                {m.content}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-md bg-cyan-100 text-[#F58220] grid place-items-center shrink-0">
                  <User size={16} />
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex gap-2.5">
              <div className="w-8 h-8 rounded-md bg-[#F58220] text-white grid place-items-center"><Bot size={16}/></div>
              <div className="bg-slate-100 border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-500 inline-flex gap-1">
                <Dot delay="0s"/><Dot delay=".2s"/><Dot delay=".4s"/>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 p-4 bg-white">
          <div className="flex flex-wrap gap-2 mb-3">
            {suggestions.map((s) => (
              <button
                key={s}
                data-testid={`ai-sugg-${s.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => send(s)}
                disabled={busy}
                className="text-xs border border-slate-200 rounded-full px-3 py-1.5 text-slate-700 hover:border-[#F58220] hover:bg-[#FFF0DF] disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
          <form onSubmit={onSubmit} className="flex gap-2">
            <Input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the analyst…"
              disabled={busy}
              className="h-11"
            />
            <Button data-testid="ai-send" type="submit" disabled={busy} className="bg-[#F58220] hover:bg-[#E36F0E] text-white h-11 px-5">
              <Send size={16} />
            </Button>
          </form>
          {err && <p className="mt-2 text-xs text-rose-600">{err}</p>}
        </div>
      </Card>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-slate-400 inline-block"
      style={{ animation: "bounce 1s infinite", animationDelay: delay }}
    />
  );
}
